import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LabelEntry, LabelTemplateModel } from '../models/label-entry.model';
import Swal from 'sweetalert2';
import { ApiService } from './api.service';
import { TxSetupResult, TxSetupService } from './tx-setup.service';
import { ManagerialService } from './managerial.service';

declare var dymo: any;

interface LabelPreview {
  clinicInfo: string;
  clientInfo: string;
  doctorInfo: string;
  productInfo: string;
  instructions: string;
  dateInfo: string;
}

@Injectable({
  providedIn: 'root'
})
export class LabelEntryService {
  private baseUrl: string = 'procedure';
  private labelTemplatePath: string = 'C:\\Itimpact\\Family Pet\\Print';
  private dymoAddIn: any = null;
  private dymoLabels: any = null;
  private printerReady: boolean = false;

  private formResult: TxSetupResult;

  constructor(
    private api: ApiService,
    private http: HttpClient,
    private txSetup: TxSetupService,
    private managerialService: ManagerialService
  ) { }


  async LabelEntry(labelEntry: LabelEntry, Status: string, lngSeq: number,LinkName: string): Promise<LabelEntry | null> {
    try {
      // Initialize Dymo printer and load template before processing label type
      // const dymoInitialized = await this.InitializeDymoLabel();
      // if (!dymoInitialized) {
      //   await Swal.fire({
      //     title: 'Printer Not Available',
      //     text: 'Could not initialize Dymo label printer. Please ensure the printer is connected and try again.',
      //     icon: 'error',
      //     confirmButtonText: 'OK'
      //   });
      //   return null;
      // }

      let clinicAddress = '';
      let clientInfo = '';
      let doctorName = '';
      let productInfo = '';

      try {
        // Get clinic information
        const clinicData = await this.GetClinicInfo(labelEntry.lngClientNo).toPromise().catch(() => ({}));
        if (clinicData && typeof clinicData === 'object') {
          clinicAddress = (clinicData as any)?.clinicInfo || '';
        }

        // Get doctor information
        const doctorData = await this.GetDoctorInfo(labelEntry.intDocNo).toPromise().catch(() => ({}));
        if (doctorData && typeof doctorData === 'object') {
          doctorName = (doctorData as any)?.doctorName || '';
          if (!doctorName.includes('DVM')) {
            doctorName = doctorName + ', DVM';
          }
        }

        // Build client info from labelEntry
        clientInfo = `${labelEntry.petName} - ${labelEntry.specie}`;

        // Build product info
        productInfo = `Unit Disp.: ${labelEntry.sglUPI} ${labelEntry.unitLabel}`;
        if (labelEntry.labelCode) {
          productInfo += `\n${labelEntry.labelCode}`;
        }
      } catch (err) {
        console.warn('Warning: Could not fully load clinic/doctor/client information:', err);
      }

      let result: LabelEntry | null = null;

      try {
        switch (labelEntry.strLabelType) {
          case 'B':
            result = await this.BlankLabel(labelEntry, lngSeq, clinicAddress, clientInfo, doctorName, productInfo);
            break;
          case 'R':
            result = await this.RefillLabel(labelEntry, lngSeq, clinicAddress, clientInfo, doctorName, productInfo);
            break;
          case 'W':
            result = await this.ShowWrittenRxModal(labelEntry, lngSeq);
            break;
          default:
            result = await this.MakeLabel(labelEntry, lngSeq, Status, clinicAddress, clientInfo, doctorName, productInfo, LinkName);
            break;
        }
      } finally {
        // Clean up Dymo resources after modal processing
        await this.CleanupDymo();
      }

      return result;
    } catch (err) {
      console.error('Error in LabelEntry:', err);
      await this.CleanupDymo();
      return null;
    }
  }

  private async MakeLabel(
    labelEntry: LabelEntry,
    lngSeq: number,
    Status: string,
    clinicAddress: string,
    clientInfo: string,
    doctorName: string,
    productInfo: string,
    LinkName: string
  ): Promise<LabelEntry | null> {
    try {
      let sglTotal = 0;
      let strLabel: string = '';
      let strReason: string = '';
      let dteExp = null;
      let dteRxExp = null;
      let refillCount = 0;
      let prescData: LabelTemplateModel | null = null;

      labelEntry.labelCode=await this.SelectLabelCode(labelEntry.labelCode,LinkName);

      // Get prescription text from database
      const response = await this.api.get(`${this.baseUrl}/GetLabelTemplate?labelCode=${labelEntry.labelCode}`).toPromise();

      // Handle response wrapped in data property or direct object
      const templateData = (response as any)?.data || response;
      prescData = templateData as LabelTemplateModel;

      if (prescData?.presc_text) {
        strLabel = prescData.presc_text;
        strReason = prescData.presc_reason_desc || '';
      }

      // Check if label contains placeholders (~)
      const hasPlaceholders = !!(strLabel && strLabel.includes('~'));

      /* if (!formResult) {
        return null;
      } */

      if (hasPlaceholders) {
        // Replace placeholders in label
        this.formResult = await this.txSetup.ShowRxSetupModal(4, labelEntry.unitLabel);

        strLabel = this.txSetup.replaceLabelPlaceholders(
          strLabel,
          this.formResult.units,
          this.formResult.noPerDay,
          this.formResult.days,
          this.formResult.total
        );

        // Calculate total units to dispense
        sglTotal = this.txSetup.calculateTotalDispense(
          this.formResult.units,
          this.formResult.noPerDay,
          this.formResult.days,
          this.formResult.total
        );

        dteExp = this.formResult.productExpDate;

        const escapedLabel = strLabel.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const { value: labelForm } = await Swal.fire({
          title: 'Label Entry',
          customClass: { popup: 'swal-wide-popup' },
          width: '520px',
          heightAuto: false,
          scrollbarPadding: false,
          html: `
            <div style="text-align:left; padding: 4px 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
              <p style="margin: 0 0 6px 0; font-size:12px; font-weight:600; color:#374151;">
                Enter label instructions on up to 3 lines of up to 50 characters each:
              </p>
              <textarea id="ml-instructions"
                rows="3"
                maxlength="150"
                style="width:100%; padding:6px 8px; font-size:13px; font-family:'Courier New', monospace;
                       border:1px solid #94a3b8; border-radius:3px; resize:none; box-sizing:border-box;
                       background:#fff; color:#111; line-height:1.5;"
              >${escapedLabel}</textarea>
              <p style="margin: 4px 0 0 0; font-size:11px; color:#6b7280;">
                Note: width of text box is 50 characters. For best results do NOT use CAPS lock.
              </p>
            </div>`,
          focusConfirm: false,
          showCancelButton: true,
          confirmButtonText: 'Exit',
          cancelButtonText: 'Cancel',
          didOpen: (modal) => {
            const ta = modal.querySelector('#ml-instructions') as HTMLTextAreaElement;
            ta?.focus();
            ta?.setSelectionRange(ta.value.length, ta.value.length);
          },
          preConfirm: () => {
            return (document.getElementById('ml-instructions') as HTMLTextAreaElement).value;
          }
        });

        if (labelForm === undefined) return null;
        strLabel = labelForm;

      } else {
        const oneYearOut = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
        const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
        const escapedLabel = strLabel.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

        const { value: form } = await Swal.fire({
          title: 'Label Entry',
          customClass: { popup: 'swal-wide-popup' },
          width: '520px',
          heightAuto: false,
          scrollbarPadding: false,
          html: `
            <div style="text-align:left; padding: 4px 8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;">
              <p style="margin: 0 0 6px 0; font-size:12px; font-weight:600; color:#374151;">
                Enter label instructions on up to 3 lines of up to 50 characters each:
              </p>
              <textarea id="ml-instructions"
                rows="3"
                maxlength="150"
                style="width:100%; padding:6px 8px; font-size:13px; font-family:'Courier New', monospace;
                       border:1px solid #94a3b8; border-radius:3px; resize:none; box-sizing:border-box;
                       background:#fff; color:#111; line-height:1.5;"
              >${escapedLabel}</textarea>
              <p style="margin: 4px 0 8px 0; font-size:11px; color:#6b7280;">
                Note: width of text box is 50 characters. For best results do NOT use CAPS lock.
              </p>
              <div style="display:flex; align-items:center; gap:10px;">
                <label style="flex: 0 0 38%; font-size:12px; font-weight:600; color:#374151;">Use / Reason:</label>
                <input id="ml-reason" type="text" placeholder="e.g. Infection" maxlength="20" value="${strReason}"
                  style="flex: 1; height:28px; padding:2px 8px; font-size:12px; border:1px solid #94a3b8; border-radius:3px; box-sizing:border-box;">
              </div>
            </div>`,
          focusConfirm: false,
          showCancelButton: true,
          confirmButtonText: 'Exit',
          cancelButtonText: 'Cancel',
          didOpen: (modal) => {
            const ta = modal.querySelector('#ml-instructions') as HTMLTextAreaElement;
            ta?.focus();
            ta?.setSelectionRange(ta.value.length, ta.value.length);
          },
          preConfirm: () => {
            const instructions = (document.getElementById('ml-instructions') as HTMLTextAreaElement).value;
            const reason = (document.getElementById('ml-reason') as HTMLInputElement).value.trim();
            return { instructions, reason };
          }
        });

        if (!form) return null;

        this.formResult = {
          units: 0,
          noPerDay: 0,
          days: 1,
          total: 1,
          totalUnits: labelEntry.sglUPI || 0,
          productExpDate: new Date(),
          reason: form.reason,
          instructions: form.instructions,
          isPRN: false,
          refillCount: 0,
          refillExpireDate: oneYearOut,
          nextRefillDate: tomorrow
        };
        sglTotal = this.formResult.totalUnits;
        strLabel = form.instructions;
      }

      dteRxExp = this.formResult.refillExpireDate;
      refillCount = this.formResult.refillCount;

      labelEntry.sglUPI = sglTotal;

      // Build product info string
      let strProductInfo = `${labelEntry.labelCode || 'Product'}\nUnit Disp.: ${sglTotal.toFixed(2)}  ${labelEntry.unitLabel}`;

      if (dteExp && dteExp.getTime() !== new Date().getTime()) {
        strProductInfo += `   Exp.: ${(dteExp.getMonth() + 1).toString().padStart(2, '0')}/${dteExp.getFullYear()}`;
      }

      if (this.formResult.reason) {
        strProductInfo += `   USE FOR: ${this.formResult.reason}`;
      }

      // Add refill information
      if (refillCount < 0) {
        strProductInfo += `\n          Refill As Needed till ${dteRxExp?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`;
      } else if (refillCount === 1) {
        strProductInfo += `\n          1 refill remaining before ${dteRxExp?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`;
      } else if (refillCount > 1) {
        strProductInfo += `\n          ${refillCount} refills remaining before ${dteRxExp?.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`;
      } else {
        strProductInfo += `\n          NO refills remaining`;
      }

      labelEntry.strInstructions = this.formResult.instructions || strProductInfo;

      const setupSuccess = await this.SetupNormalLabel(labelEntry, clinicAddress, clientInfo, doctorName, strProductInfo);
      if (!setupSuccess) {
        await Swal.fire({
          title: 'Setup Error',
          text: 'Failed to setup label. Please try again.',
          icon: 'error'
        });
        return null;
      }

      await this.HandlePrintAndDeferLogic(lngSeq, Status);

      return labelEntry;
    } catch (err) {
      console.error('Error in MakeLabel:', err);
      await Swal.fire({
        title: 'Error',
        text: 'An error occurred while processing the label.',
        icon: 'error'
      });
      return null;
    }
  }


  private async BlankLabel(
    labelEntry: LabelEntry,
    lngSeq: number,
    clinicAddress: string,
    clientInfo: string,
    doctorName: string,
    productInfo: string
  ): Promise<LabelEntry | null> {
    try {
      const { value: form } = await Swal.fire({
        title: 'Blank Label Entry',
        customClass: { popup: 'swal-wide-popup' },
        width: '600px',
        heightAuto: false,
        scrollbarPadding: false,
        html: `
          <div style="text-align:left;">
            <div style="margin-bottom:12px;">
              <label style="display:block; margin-bottom:4px; font-weight:500;">Product Name:</label>
              <input id="bl-product" class="swal1-input" type="text" placeholder="Product name" style="width:100%; padding:8px; font-size:14px; border:1px solid #ddd; border-radius:3px;">
            </div>
            <div style="margin-bottom:12px;">
              <label style="display:block; margin-bottom:4px; font-weight:500;">Method of Issue (e.g. TAB, ML):</label>
              <input id="bl-units" class="swal1-input" type="text" placeholder="e.g. TAB" maxlength="5" style="width:100%; padding:8px; font-size:14px; border:1px solid #ddd; border-radius:3px;">
            </div>
            <div style="margin-bottom:12px;">
              <label style="display:block; margin-bottom:4px; font-weight:500;" id="bl-units-label">Units to Prescribe:</label>
              <input id="bl-total" class="swal1-input" type="number" min="0.01" step="0.01" style="width:100%; padding:8px; font-size:14px; border:1px solid #ddd; border-radius:3px;">
            </div>
            <div style="margin-bottom:12px;">
              <label style="display:block; margin-bottom:4px; font-weight:500;">Use / Reason (optional):</label>
              <input id="bl-reason" class="swal1-input" type="text" placeholder="e.g. Infection" maxlength="20" style="width:100%; padding:8px; font-size:14px; border:1px solid #ddd; border-radius:3px;">
            </div>
          </div>`,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        didOpen: (modal) => {
          const unitsInput = modal.querySelector('#bl-units') as HTMLInputElement;
          const unitsLabelElement = modal.querySelector('#bl-units-label') as HTMLElement;

          unitsInput?.addEventListener('change', () => {
            if (unitsInput.value.trim()) {
              if (unitsLabelElement) {
                unitsLabelElement.textContent = `Enter number of ${unitsInput.value.trim()} to prescribe:`;
              }
            }
          });
        },
        preConfirm: () => {
          const product = (document.getElementById('bl-product') as HTMLInputElement).value.trim();
          const units = (document.getElementById('bl-units') as HTMLInputElement).value.trim();
          const total = parseFloat((document.getElementById('bl-total') as HTMLInputElement).value);
          const reason = (document.getElementById('bl-reason') as HTMLInputElement).value.trim();

          if (!product) {
            Swal.showValidationMessage('Product name is required');
            return false;
          }
          if (!units) {
            Swal.showValidationMessage('Method of issue is required');
            return false;
          }
          if (isNaN(total) || total <= 0) {
            Swal.showValidationMessage('Units must be a positive number');
            return false;
          }

          return { product, units, total, reason };
        }
      });

      if (!form) return null;

      const strProduct = form.product.toUpperCase();
      const strUnits = form.units;
      const sglTotal = form.total;
      const strReason = form.reason;

      // Build product info
      let blankProductInfo = `${strProduct}\nUnit Disp.: ${sglTotal.toFixed(2)}  ${strUnits}`;
      if (strReason) {
        blankProductInfo += `   USE FOR: ${strReason}`;
      }

      labelEntry.sglTotal = sglTotal;
      labelEntry.unitLabel = strUnits;
      labelEntry.labelCode = 'BLANK';

      const setupSuccess = await this.SetupBlankLabel(labelEntry, clinicAddress, clientInfo, doctorName, blankProductInfo);
      if (!setupSuccess) {
        await Swal.fire({
          title: 'Setup Error',
          text: 'Failed to setup blank label. Please try again.',
          icon: 'error'
        });
        return null;
      }

      await this.HandlePrintAndDeferLogic(lngSeq, 'P');

      return labelEntry;
    } catch (err) {
      console.error('Error in ShowBlankLabelModal:', err);
      await Swal.fire({
        title: 'Error',
        text: 'An error occurred while processing the blank label.',
        icon: 'error'
      });
      return null;
    }
  }

  private async RefillLabel(
    labelEntry: LabelEntry,
    lngSeq: number,
    clinicAddress: string,
    clientInfo: string,
    doctorName: string,
    productInfo: string
  ): Promise<LabelEntry | null> {
    try {
      let strProduct = '';

      // Get product description from database (equivalent to tblInventory in VB6)
      try {
        const productData = await this.api.post(`${this.baseUrl}/GetProductDescription`, {
          itemNo: labelEntry.intItemNo,
          clinicNo: labelEntry.lngClientNo
        }).toPromise() as any;

        if (productData) {
          strProduct = productData.item_desc || '';
        }
      } catch (err) {
        console.warn('Could not get product description:', err);
        strProduct = labelEntry.labelCode || 'Product';
      }

      // Ask for refill instructions
      const { value: instructions } = await Swal.fire({
        title: 'Refill Label Entry',
        customClass: { popup: 'swal-wide-popup' },
        width: '600px',
        heightAuto: false,
        scrollbarPadding: false,
        html: `
          <div style="text-align:left;">
            <label style="display:block; margin-bottom:8px; font-weight:500;">Enter refill label instructions (max 150 characters):</label>
            <textarea id="rf-instructions" style="width:100%; height:120px; padding:10px; font-size:14px; border:1px solid #ddd; border-radius:3px; font-family:sans-serif; resize:vertical;" maxlength="150" placeholder="Enter instructions here..."></textarea>
            <div style="margin-top:8px; font-size:12px; color:#666;">
              <span id="rf-char-count">0</span>/150 characters
            </div>
          </div>`,
        showCancelButton: true,
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        focusConfirm: false,
        didOpen: (modal) => {
          const textarea = modal.querySelector('#rf-instructions') as HTMLTextAreaElement;
          const charCount = modal.querySelector('#rf-char-count') as HTMLElement;

          textarea?.addEventListener('input', () => {
            if (charCount) {
              charCount.textContent = textarea.value.length.toString();
            }
          });

          textarea?.focus();
        },
        preConfirm: () => {
          const textarea = document.querySelector('#rf-instructions') as HTMLTextAreaElement;
          const value = textarea?.value.trim() || '';

          if (!value) {
            Swal.showValidationMessage('Instructions cannot be empty');
            return false;
          }
          if (value.length > 150) {
            Swal.showValidationMessage('Instructions must not exceed 150 characters');
            return false;
          }

          return value;
        }
      });

      if (!instructions) return null;

      labelEntry.strInstructions = instructions;

      // Build refill product info
      // Format: ProductName\nUnit Disp.: X Unit_Label[LabelCode]
      let strRefillProductInfo = strProduct;
      strRefillProductInfo += `\nUnit Disp.: ${labelEntry.sglUPI} ${labelEntry.unitLabel}`;

      if (labelEntry.labelCode) {
        strRefillProductInfo += `\n${labelEntry.labelCode}`;
      }

      const setupSuccess = await this.SetupRefillLabel(labelEntry, clinicAddress, clientInfo, doctorName, strRefillProductInfo);
      if (!setupSuccess) {
        await Swal.fire({
          title: 'Setup Error',
          text: 'Failed to setup refill label. Please try again.',
          icon: 'error'
        });
        return null;
      }

      await this.HandlePrintAndDeferLogic(lngSeq, 'P');

      return labelEntry;
    } catch (err) {
      console.error('Error in RefillLabel:', err);
      await Swal.fire({
        title: 'Error',
        text: 'An error occurred while processing the refill label.',
        icon: 'error'
      });
      return null;
    }
  }

  private async ShowWrittenRxModal(labelEntry: LabelEntry, lngSeq: number): Promise<LabelEntry | null> {
    const { value: instructions } = await Swal.fire({
      title: 'Written Rx Instructions',
      customClass: { popup: 'swal-wide-popup' },
      width: '600px',
      heightAuto: false,
      scrollbarPadding: false,
      html: `
        <div style="text-align:left;">
          <label style="display:block; margin-bottom:8px; font-weight:500;">Enter prescription instructions (max 150 characters):</label>
          <textarea id="wr-instructions" style="width:100%; height:120px; padding:10px; font-size:14px; border:1px solid #ddd; border-radius:3px; font-family:sans-serif; resize:vertical;" maxlength="150" placeholder="Enter instructions here..."></textarea>
          <div style="margin-top:8px; font-size:12px; color:#666;">
            <span id="wr-char-count">0</span>/150 characters
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      focusConfirm: false,
      didOpen: (modal) => {
        const textarea = modal.querySelector('#wr-instructions') as HTMLTextAreaElement;
        const charCount = modal.querySelector('#wr-char-count') as HTMLElement;

        textarea?.addEventListener('input', () => {
          if (charCount) {
            charCount.textContent = textarea.value.length.toString();
          }
        });

        textarea?.focus();
      },
      preConfirm: () => {
        const textarea = document.querySelector('#wr-instructions') as HTMLTextAreaElement;
        const value = textarea?.value.trim() || '';

        if (!value) {
          Swal.showValidationMessage('Instructions cannot be empty');
          return false;
        }
        if (value.length > 150) {
          Swal.showValidationMessage('Instructions must not exceed 150 characters');
          return false;
        }

        return value;
      }
    });

    if (!instructions) return null;

    labelEntry.strInstructions = instructions;
    return labelEntry;
  }

  /**
   * Setup Blank Label (equivalent to BlankLabel() in VB6)
   * Sets Dymo fields for blank label type
   */
  async SetupBlankLabel(labelEntry: LabelEntry, clinicAddress: string, clientInfo: string, doctorName: string, productInfo: string): Promise<boolean> {
    try {
      // // IDymoLabels.SetField calls from BlankLabel() in VB6
      // await this.SetFields({
      //   'Clinic Address': clinicAddress,
      //   'Client Info': clientInfo,
      //   'Doctor': doctorName,
      //   'Date': new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
      //   'Product Info': productInfo,
      //   'Details': labelEntry.strInstructions
      // });

      // // Save the label file (IDymoAddIn.SaveAs)
      // const filePath = this.getLabelFilePath(labelEntry.lngTranSeq);
      // return await this.SaveLabelFile(filePath);
      return true;
    } catch (err) {
      console.error('Error in SetupBlankLabel:', err);
      return false;
    }
  }

  /**
   * Setup Refill Label (equivalent to RefillLabel() in VB6)
   * Sets Dymo fields for refill label type
   */
  async SetupRefillLabel(labelEntry: LabelEntry, clinicAddress: string, clientInfo: string, doctorName: string, productInfo: string): Promise<boolean> {
    try {
      // // IDymoLabels.SetField calls from RefillLabel() in VB6
      // await this.SetFields({
      //   'Clinic Address': clinicAddress,
      //   'Client Info': clientInfo,
      //   'Doctor': doctorName,
      //   'Date': new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
      //   'Product Info': productInfo,
      //   'Details': labelEntry.strInstructions
      // });

      // // Save the label file (IDymoAddIn.SaveAs)
      // const filePath = this.getLabelFilePath(labelEntry.lngTranSeq);
      // return await this.SaveLabelFile(filePath);
      return true;
    } catch (err) {
      console.error('Error in SetupRefillLabel:', err);
      return false;
    }
  }

  /**
   * Setup Normal Label (equivalent to MakeLabel() in VB6)
   * Sets Dymo fields for normal Rx label type
   */
  async SetupNormalLabel(labelEntry: LabelEntry, clinicAddress: string, clientInfo: string, doctorName: string, productInfo: string): Promise<boolean> {
    try {
      // // IDymoLabels.SetField calls from MakeLabel() in VB6
      // await this.SetFields({
      //   'Clinic Address': clinicAddress,
      //   'Client Info': clientInfo,
      //   'Doctor': doctorName,
      //   'Date': new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }),
      //   'Product Info': productInfo,
      //   'Details': labelEntry.strInstructions
      // });

      // // Save the label file (IDymoAddIn.SaveAs)
      // const filePath = this.getLabelFilePath(labelEntry.lngTranSeq);
      // return await this.SaveLabelFile(filePath);
      return true;
    } catch (err) {
      console.error('Error in SetupNormalLabel:', err);
      return false;
    }
  }

  async CreateDymoObjects(): Promise<boolean> {
    return new Promise((resolve) => {
      this.api.post(`${this.baseUrl}/CreateDymoObjects`, {})
        .subscribe({
          next: (res: any) => resolve(res?.success ?? false),
          error: () => resolve(false)
        });
    });
  }

  async PrintALabel(filePath: string, noOfLabels: number): Promise<void> {
    return new Promise((resolve, reject) => {
      this.api.post(`${this.baseUrl}/PrintALabel`, { filePath, noOfLabels })
        .subscribe({
          next: () => resolve(),
          error: (err) => reject(err)
        });
    });
  }

  async DB_Update(sql: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.api.post(`${this.baseUrl}/ExecuteSQL`, { sql })
        .subscribe({
          next: () => resolve(),
          error: (err) => reject(err)
        });
    });
  }

  async UpdateTempPrescRefill(seq: number, increment: number): Promise<void> {
    const sql = `UPDATE tblTempPresc SET refill_count = refill_count + ${increment} WHERE transact_sequence = ${seq} AND refill_count >= 0`;
    return this.DB_Update(sql);
  }

  async HandlePrintAndDeferLogic(seq: number, Status: string): Promise<void> {
    if (Status === 'P') {
      const printResult = await Swal.fire({
        text: 'Do you want to print the label now?',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      });

      if (printResult.isConfirmed) {
        await this.PrintALabel('', 1);
      } else {
        const deferResult = await Swal.fire({
          title: 'Confirm Future Rx',
          text: 'Create a Rx to be dispensed later?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        });
        if (deferResult.isConfirmed) {
          await this.UpdateTempPrescRefill(seq, 1);
        }
      }
    }
  }

  /**
   * cmdCancel_Click equivalent - Cancel label entry
   */
  async cancelLabelEntry(seq: number): Promise<void> {
    try {
      // UPDATE tblTransaction SET status = 'R' WHERE tran_sequence = seq
      await this.DB_Update(`UPDATE tblTransaction SET status = 'R' WHERE tran_sequence = ${seq}`);

      // DELETE FROM tblTempPresc WHERE transact_sequence = seq
      await this.DB_Update(`DELETE FROM tblTempPresc WHERE transact_sequence = ${seq}`);
    } catch (err) {
      console.error('Error in cancelLabelEntry:', err);
      throw err;
    }
  }

  /**
   * cmdExit_Click equivalent - Save and exit label entry
   */
  async exitLabelEntry(labelEntry: LabelEntry, seq: number, instructions: string, docNo: number, totalUnits: number): Promise<void> {
    try {
      // For normal labels (not blank), update the database
      const sql = `UPDATE tblTransaction SET status = 'P', unit = ${totalUnits}, attending = ${docNo}, done_by = ${docNo} WHERE tran_sequence = ${seq}`;
      await this.DB_Update(sql);

      // Update the dispensing instructions
      const instrSql = `UPDATE tblTempPresc SET instructions = '${instructions.replace(/'/g, "''")}' WHERE transact_sequence = ${seq}`;
      await this.DB_Update(instrSql);
    } catch (err) {
      console.error('Error in exitLabelEntry:', err);
      throw err;
    }
  }

  /**
   * Check if text is valid and word wrap if necessary (up to 3 lines, 50 chars each)
   */
  checkAndWrapText(text: string): { valid: boolean; wrappedText: string } {
    if (text.length > 150) {
      return { valid: false, wrappedText: text };
    }

    const lines: string[] = [];
    let remaining = text;

    for (let i = 0; i < 3 && remaining; i++) {
      let line = remaining.substring(0, 50);

      if (remaining.length > 50) {
        // Find last space in the 50-char window
        let lastSpace = line.lastIndexOf(' ');
        if (lastSpace > 0) {
          line = line.substring(0, lastSpace);
          remaining = remaining.substring(lastSpace + 1);
        } else {
          remaining = remaining.substring(50);
        }
      } else {
        remaining = '';
      }

      lines.push(line);
    }

    return { valid: true, wrappedText: lines.join('\n') };
  }

  /**
   * Generate label file path
   */
  getLabelFilePath(seq: number): string {
    return `C:\\TmpFile\\Drug_Labels\\L${seq}.LWL`;
  }

  /**
   * Generate label preview data for display
   */
  async GenerateLabelPreview(labelEntry: LabelEntry): Promise<LabelPreview | null> {
    try {
      const preview: LabelPreview = {
        clinicInfo: '',
        clientInfo: `${labelEntry.petName} - ${labelEntry.specie}`,
        doctorInfo: '',
        productInfo: '',
        instructions: labelEntry.strInstructions,
        dateInfo: new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
      };

      // Get clinic information from backend
      const clinicData = await this.GetClinicInfo(labelEntry.lngClientNo).toPromise().catch(() => ({}));
      if (clinicData && typeof clinicData === 'object') {
        preview.clinicInfo = (clinicData as any)?.clinicInfo || '';
      }

      // Get doctor information from backend
      const doctorData = await this.GetDoctorInfo(labelEntry.intDocNo).toPromise().catch(() => ({}));
      if (doctorData && typeof doctorData === 'object') {
        preview.doctorInfo = (doctorData as any)?.doctorName || '';
      }

      // Build product info
      let productInfo = '';
      if (labelEntry.strLabelType === 'B') {
        productInfo = `Unit Disp.: ${labelEntry.sglTotal} ${labelEntry.unitLabel}`;
      } else {
        productInfo = `Unit Disp.: ${labelEntry.sglUPI} ${labelEntry.unitLabel}`;
      }

      if (labelEntry.labelCode) {
        productInfo += `\n${labelEntry.labelCode}`;
      }

      preview.productInfo = productInfo;

      return preview;
    } catch (err) {
      console.error('Error generating label preview:', err);
      return null;
    }
  }

  /**
   * Show label preview modal (optional step before printing)
   */
  async ShowLabelPreviewModal(preview: LabelPreview): Promise<boolean> {
    const result = await Swal.fire({
      title: 'Label Preview',
      customClass: { popup: 'swal-wide-popup' },
      width: '600px',
      heightAuto: false,
      scrollbarPadding: false,
      html: `
        <div style="text-align:left;border:1px solid #ccc;padding:15px;background:#f9f9f9;margin-bottom:10px;">
          <div style="font-weight:bold;margin-bottom:8px;">${preview.clinicInfo}</div>
          <div style="margin-bottom:8px;"><strong>Client:</strong> ${preview.clientInfo}</div>
          <div style="margin-bottom:8px;"><strong>Doctor:</strong> ${preview.doctorInfo}</div>
          <div style="margin-bottom:8px;"><strong>Date:</strong> ${preview.dateInfo}</div>
          <hr style="margin:8px 0;">
          <div style="margin-bottom:8px;"><strong>Product Info:</strong><br/>${preview.productInfo.replace(/\n/g, '<br/>')}</div>
          <div style="margin-bottom:8px;"><strong>Instructions:</strong><br/>${preview.instructions}</div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Print Label',
      cancelButtonText: 'Cancel',
      icon: 'info',
      didOpen: (modal) => {
        const htmlContainer = modal.querySelector('.swal-html-container') as HTMLElement;
        if (htmlContainer) {
          htmlContainer.style.maxHeight = '70vh';
          htmlContainer.style.overflowY = 'auto';
        }
      }
    });

    return result.isConfirmed;
  }

  /**
   * Handle print and defer logic for normal rx (separate from preview)
   */
  async PrintAndDeferDialog(seq: number): Promise<'print' | 'defer' | 'cancel'> {
    const printResult = await Swal.fire({
      title: 'Print Label',
      text: 'Do you want to print the label now?',
      showCancelButton: true,
      confirmButtonText: 'Yes, Print',
      cancelButtonText: 'No, Defer',
      icon: 'question'
    });

    if (printResult.isConfirmed) {
      return 'print';
    }

    const deferResult = await Swal.fire({
      title: 'Defer Prescription',
      text: 'Create a Rx to be dispensed later?',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      icon: 'question'
    });

    return deferResult.isConfirmed ? 'defer' : 'cancel';
  }

  /**
   * Process print request (independent of preview)
   */
  async ProcessPrintLabel(filePath: string, noOfLabels: number = 1): Promise<void> {
    try {
      await this.PrintALabel(filePath, noOfLabels);
      await Swal.fire({
        title: 'Success',
        text: 'Label printed successfully',
        icon: 'success',
        timer: 2000
      });
    } catch (err) {
      console.error('Error printing label:', err);
      await Swal.fire({
        title: 'Print Error',
        text: 'Failed to print label',
        icon: 'error'
      });
      throw err;
    }
  }

  /**
   * Get clinic information from backend
   */
  GetClinicInfo(clientNo: number) {
    return this.api.post(`${this.baseUrl}/GetClinicInfo`, { clientNo });
  }

  /**
   * Get doctor information from backend
   */
  GetDoctorInfo(doctorNo: number) {
    return this.api.post(`${this.baseUrl}/GetDoctorInfo`, { doctorNo });
  }

  /**
   * Validate and save label (for normal Rx types)
   */
  async ValidateAndSaveLabel(labelEntry: LabelEntry, seq: number, totalUnits: number): Promise<boolean> {
    try {
      const validation = this.checkAndWrapText(labelEntry.strInstructions);
      if (!validation.valid) {
        await Swal.fire({
          title: 'Text Too Long',
          text: 'Label text is too long. Only the first 150 characters will be printed.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continue',
          cancelButtonText: 'Edit'
        });
      }

      return true;
    } catch (err) {
      console.error('Error validating label:', err);
      return false;
    }
  }

  /**
   * Generate label file for printing
   */
  async GenerateLabelFile(labelContent: any): Promise<string> {
    try {
      const filePath = this.getLabelFilePath(labelContent.seq);
      await this.api.post(`${this.baseUrl}/GenerateLabelFile`, {
        filePath,
        labelContent
      }).toPromise();
      return filePath;
    } catch (err) {
      console.error('Error generating label file:', err);
      throw err;
    }
  }

/**
   * Initialize Dymo printer and load label template (equivalent to Form_Load in VB6)
   * This creates IDymoAddIn and IDymoLabels objects via backend and loads the .LWT file
   */
  async InitializeDymoLabel(clinicType: string = 'FPC'): Promise<boolean> {
    try {
      // Step 1: Create Dymo OLE objects (CreateOLEObjects in VB6)
      const dymoCreated = await this.CreateDymoObjects();
      if (!dymoCreated) {
        console.warn('Failed to create Dymo OLE objects on backend');
        this.printerReady = false;
        return false;
      }

      // Step 2: Determine template file based on clinic type
      // Path structure: C:\Itimpact\Family Pet\Print\pic\FPC Rx Label.LWT
      //                 or C:\Itimpact\Family Pet\Print\pic\PetVet Rx Label.LWT
      const templateFileName = clinicType === 'PetVet' ? 'PetVet Rx Label.LWT' : 'FPC Rx Label.LWT';
      const templatePath = `${this.labelTemplatePath}\\pic\\${templateFileName}`;

      // Step 3: Open template file (IDymoAddIn.Open strFile in VB6)
      const templateLoaded = await this.api.post(`${this.baseUrl}/OpenLabelTemplate`, {
        templatePath
      }).toPromise().then((res: any) => res?.success ?? false).catch((err) => {
        console.error(`Failed to open template: ${templatePath}`, err);
        return false;
      });

      if (!templateLoaded) {
        console.warn(`Cannot find ${templatePath} file!`);
        this.printerReady = false;
        return false;
      }

      this.printerReady = true;
      return true;
    } catch (err) {
      console.error('Error initializing Dymo label:', err);
      this.printerReady = false;
      return false;
    }
  }

  /**
   * Set a field value in the Dymo label (equivalent to IDymoLabels.SetField in VB6)
   * Example: SetField("Clinic Address", "123 Main St...")
   *          SetField("Doctor", "Dr. Smith, DVM")
   *          SetField("Details", "Instructions text")
   */
  async SetField(fieldName: string, value: string): Promise<void> {
    try {
      if (!this.printerReady) {
        console.warn('Dymo printer not ready, skipping SetField');
        return;
      }

      await this.api.post(`${this.baseUrl}/SetLabelField`, {
        fieldName,
        value
      }).toPromise().catch(err => {
        console.error(`Failed to set field '${fieldName}':`, err);
      });
    } catch (err) {
      console.error('Error in SetField:', err);
    }
  }

  /**
   * Set multiple label fields at once (batch SetField calls)
   */
  async SetFields(fields: { [key: string]: string }): Promise<void> {
    try {
      if (!this.printerReady) {
        console.warn('Dymo printer not ready, skipping SetFields');
        return;
      }

      await this.api.post(`${this.baseUrl}/SetLabelFields`, {
        fields
      }).toPromise().catch(err => {
        console.error('Failed to set label fields:', err);
      });
    } catch (err) {
      console.error('Error in SetFields:', err);
    }
  }

  /**
   * Save the label file (equivalent to IDymoAddIn.SaveAs in VB6)
   * Saves the populated label to a .LWL file for printing
   */
  async SaveLabelFile(filePath: string): Promise<boolean> {
    try {
      if (!this.printerReady) {
        console.warn('Dymo printer not ready, skipping SaveAs');
        return false;
      }

      const saved = await this.api.post(`${this.baseUrl}/SaveLabelFile`, {
        filePath
      }).toPromise().then((res: any) => res?.success ?? false).catch(err => {
        console.error(`Failed to save label file: ${filePath}`, err);
        return false;
      });

      return saved;
    } catch (err) {
      console.error('Error in SaveLabelFile:', err);
      return false;
    }
  }

  /**
   * Clean up Dymo resources (equivalent to Set IDymoAddIn = Nothing and Set IDymoLabels = Nothing)
   */
  async CleanupDymo(): Promise<void> {
    try {
      await this.api.post(`${this.baseUrl}/CleanupDymo`, {}).toPromise().catch(() => {
        // Cleanup errors are non-critical
      });
      this.dymoAddIn = null;
      this.dymoLabels = null;
      this.printerReady = false;
    } catch (err) {
      console.error('Error cleaning up Dymo:', err);
    }
  }

  async SelectLabelCode(LabelCode: string, LinkName: string): Promise<string> {
    let opt = '';
    await this.managerialService.getPrescription().then(async () => {
      const options: { [key: string]: string } = {};
      this.managerialService.prescriptions.forEach((item: any) => {
        options[item.presc_code] = item.presc_code;
      });

      const { value: selection } = await Swal.fire({
        title: "Select Label Code for " + LinkName,
        input: 'select',
        inputOptions: options,
        inputValue: LabelCode,
        showCancelButton: true,
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel'
      });

      opt = selection || LabelCode;
    }).catch(() => { });

    return opt;
  }

  //#region fpc_blank.label test print (browser-based DYMO Connect SDK)
  // Separate from the rest of this file: everything above talks to the backend, which
  // automates DYMO via OLE on the print-server machine. This label lives in src/assets,
  // so it's only reachable as a plain web URL - printed here directly from the browser
  // via the DYMO Label Framework SDK (loaded in index.html), which talks to the locally
  // running DYMO Connect service instead of going through the backend at all.

  private blankClinicLabelObj: any = null;

  private async getBlankClinicLabelObj(): Promise<any> {
    if (this.blankClinicLabelObj) {
      return this.blankClinicLabelObj;
    }

    // Cache-bust: this file is served from /assets with no content hash, so a stale
    // browser/proxy cache can otherwise keep serving an old or missing copy indefinitely.
    const labelXml = await this.http.get(`assets/data/fpc_blank.xml?v=${Date.now()}`, { responseType: 'text' }).toPromise();
    const label = dymo.label.framework.openLabelXml(labelXml);

    const objectNames: string[] = label.getObjectNames ? label.getObjectNames() : [];
    if (!objectNames.includes('clinic')) {
      throw new Error(
        `Label template did not load correctly (missing 'clinic' field, found: [${objectNames.join(', ')}]). ` +
        `This usually means the browser fetched something other than the real label file - try a hard refresh.`
      );
    }

    this.blankClinicLabelObj = label;
    return this.blankClinicLabelObj;
  }

  private checkDymoEnvironment(): Promise<{ isBrowserSupported: boolean; isFrameworkInstalled: boolean; isWebServicePresent: boolean; errorDetails?: string }> {
    return new Promise((resolve) => {
      dymo.label.framework.checkEnvironment((result: any) => resolve(result));
    });
  }

  /**
   * label.print()/printLabel() in this SDK build discards the webservice response, so a
   * resolved call only means the HTTP request reached DYMO Connect - not that the printer
   * actually printed. printAndPollStatus polls dymo.label.framework.PrintJobStatus instead,
   * which reports real outcomes (Finished/Error/PaperOut/PrinterBusy/...).
   */
  private printLabelWithStatus(label: any, printerName: string, timeoutMs: number = 10000): Promise<{ success: boolean; message?: string }> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (success: boolean, message?: string) => {
        if (settled) return;
        settled = true;
        resolve({ success, message });
      };

      const timeoutHandle = setTimeout(() => finish(false, 'Timed out waiting for the printer to report status.'), timeoutMs);

      try {
        label.printAndPollStatus(printerName, '', '', (_job: any, statusInfo: any) => {
          if (settled) return false;

          const status = statusInfo?.status;
          const PrintJobStatus = dymo.label.framework.PrintJobStatus;

          if (status === PrintJobStatus.Finished) {
            clearTimeout(timeoutHandle);
            finish(true);
            return false;
          }

          if (status === PrintJobStatus.Printing || status === PrintJobStatus.InQueue || status === PrintJobStatus.Unknown) {
            return true; // keep polling
          }

          // Error, ProcessingError, PaperOut, PrinterBusy, InvalidJobId, NotSpooled, or anything unrecognized
          clearTimeout(timeoutHandle);
          finish(false, statusInfo?.statusMessage || `Printer reported status code ${status}`);
          return false;
        }, 500);
      } catch (err: any) {
        clearTimeout(timeoutHandle);
        finish(false, err?.message);
      }
    });
  }

  private escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /**
   * Renders a collapsible "Technical details" block so the person seeing the dialog can
   * screenshot/relay the diagnostic info without needing to open the browser console.
   */
  private withDiagnostics(message: string, details: { [key: string]: any }): string {
    const rows = Object.entries(details)
      .map(([key, value]) => {
        const rendered = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
        return `<tr><td style="padding:2px 10px 2px 0; color:#6b7280; white-space:nowrap; vertical-align:top;">${this.escapeHtml(key)}</td>` +
          `<td style="padding:2px 0; word-break:break-word; white-space:pre-wrap;">${this.escapeHtml(rendered ?? '')}</td></tr>`;
      })
      .join('');

    return `
      <div style="text-align:left;">
        <p style="margin:0 0 8px 0;">${this.escapeHtml(message)}</p>
        <details style="margin-top:4px;">
          <summary style="cursor:pointer; font-size:12px; color:#2563eb;">Technical details</summary>
          <table style="margin-top:6px; font-size:11px; font-family:'Courier New', monospace; width:100%; border-collapse:collapse;">${rows}</table>
        </details>
      </div>`;
  }

  async PrintBlankClinicLabel(clinicText: string, printerName?: string): Promise<boolean> {
    let envCheck: any = null;
    let printers: any[] = [];
    let printer = printerName || '';

    try {
      if (typeof dymo === 'undefined' || !dymo.label || !dymo.label.framework) {
        await Swal.fire({
          title: 'DYMO SDK Not Available',
          text: 'The DYMO Label Framework script did not load. Make sure DYMO Connect is installed and running.',
          icon: 'error'
        });
        return false;
      }

      envCheck = await this.checkDymoEnvironment();
      if (!envCheck.isWebServicePresent) {
        console.error('DYMO environment check failed:', envCheck);
        await Swal.fire({
          title: 'DYMO Connect Not Running',
          html: this.withDiagnostics(
            'Could not reach the DYMO Connect service on this computer. Please install/start DYMO Connect, make sure the printer is plugged in, and try again. ' +
            'If DYMO Connect is already running, open https://127.0.0.1:41951 in this browser once and accept the security warning, then retry.',
            envCheck
          ),
          icon: 'error'
        });
        return false;
      }
      if (!envCheck.isBrowserSupported) {
        await Swal.fire({
          title: 'Browser Not Supported',
          html: this.withDiagnostics(envCheck.errorDetails || 'This browser is not supported by the DYMO Label Framework.', envCheck),
          icon: 'error'
        });
        return false;
      }

      const label = await this.getBlankClinicLabelObj();
      label.setObjectText('clinic', clinicText);

      if (!printer) {
        printers = dymo.label.framework.getPrinters();
        console.log('DYMO getPrinters():', printers);
        const connected = printers.filter((p: any) => p.isConnected);
        // Never prefer a disconnected printer over a connected one, regardless of type -
        // some setups report a connected DYMO device as "TapePrinter" (e.g. LabelMANAGER)
        // rather than "LabelWriterPrinter", while a stale/ghost driver entry for a printer
        // that's no longer plugged in can still show up as a disconnected LabelWriterPrinter.
        const chosen = connected.find((p: any) => p.printerType === 'LabelWriterPrinter') || connected[0];
        printer = chosen ? chosen.name : '';
      }

      if (!printer) {
        await Swal.fire({
          title: 'No Printer Found',
          html: this.withDiagnostics(
            'No DYMO printer was detected. Please check that it is connected, powered on, and visible in DYMO Connect.',
            { 'Printers seen by DYMO Connect': printers }
          ),
          icon: 'error'
        });
        return false;
      }

      console.log('Printing clinic label to:', printer);
      const printResult = await this.printLabelWithStatus(label, printer);
      if (!printResult.success) {
        console.error('DYMO print job failed:', printResult.message);
        await Swal.fire({
          title: 'Print Error',
          html: this.withDiagnostics(
            'The printer did not complete the job.',
            { 'Target printer': printer, 'Printer status': printResult.message || 'unknown error', 'Printers seen by DYMO Connect': printers }
          ),
          icon: 'error'
        });
        return false;
      }

      return true;
    } catch (err: any) {
      console.error('Error printing blank clinic label:', err);
      await Swal.fire({
        title: 'Print Error',
        html: this.withDiagnostics(
          'Failed to print the clinic label.',
          {
            'Error': err?.message || String(err),
            'Target printer': printer || '(not resolved)',
            'Printers seen by DYMO Connect': printers,
            'Environment check': envCheck
          }
        ),
        icon: 'error'
      });
      return false;
    }
  }
  //#endregion

}
